/**
 * The periodic check-in prompt.
 *
 * Deliberately not dismissible by clicking away or pressing Escape: the whole
 * point is that ignoring it counts as a miss, so offering a silent escape
 * hatch would defeat the measurement. The employee can always answer "on a
 * break", which is honest and costs them nothing beyond the productive count.
 */
import { useEffect, useState } from 'react';
import {
  Briefcase,
  Coffee,
  Car,
  MapPin,
  Pause,
  AlertTriangle,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { client, errorMessage } from '../api/client';
import { captureLocation } from '../lib/geolocation';
import type { Ping, PingResponse } from '../types';

const OPTIONS: {
  value: PingResponse;
  label: string;
  icon: typeof Briefcase;
  productive: boolean;
}[] = [
  { value: 'working', label: 'Working', icon: Briefcase, productive: true },
  // 'meeting' is deliberately absent, and deliberately still in PingResponse.
  //
  // Meeting Mode handles this case properly: it sets the pings due inside the
  // period to SKIPPED, so you are not answering a popup that should never have
  // reached you with your phone face-down in front of a client. Offering the
  // same answer here invited the worse of the two paths.
  //
  // The value stays in the enum, in the PingResponse union in types.ts, and in
  // PRODUCTIVE_RESPONSES, because pings already answered this way exist and
  // must keep counting toward availability exactly as they did. This removes a
  // choice going forward; it does not rewrite what anybody already said.
  { value: 'field_visit', label: 'Field visit', icon: MapPin, productive: true },
  { value: 'break', label: 'On a break', icon: Coffee, productive: false },
  { value: 'commuting', label: 'Commuting', icon: Car, productive: false },
  { value: 'idle', label: 'Idle', icon: Pause, productive: false },
];

interface Props {
  ping: Ping;
  onAnswered: () => void;
}

export default function PingModal({ ping, onAnswered }: Props) {
  const [selected, setSelected] = useState<PingResponse | null>(null);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(() => remainingSeconds(ping));

  useEffect(() => {
    const timer = setInterval(() => setSecondsLeft(remainingSeconds(ping)), 1000);
    return () => clearInterval(timer);
  }, [ping]);

  // Showing the deadline is the honest thing to do — the employee should know
  // exactly how long they have rather than discovering a miss after the fact.
  const expired = secondsLeft <= 0;

  async function submit() {
    if (!selected || submitting) return;
    setSubmitting(true);

    // Location is best-effort. A refused or failed fix must never block the
    // check-in itself, or a basement meeting becomes an absence.
    const geo = await captureLocation();

    try {
      await client.post(`/api/v1/attendance/ping/${ping.id}/respond`, {
        response: selected,
        activityNote: note.trim() || null,
        location: geo.ok
          ? {
              latitude: geo.location.latitude,
              longitude: geo.location.longitude,
              accuracyM: geo.location.accuracyM,
            }
          : null,
      });

      if (!geo.ok) {
        toast.warning('Checked in without location', { description: geo.message });
      } else {
        toast.success('Checked in');
      }
      onAnswered();
    } catch (error) {
      toast.error(errorMessage(error, 'Could not record your check-in'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ping-title"
    >
      <div className="w-full max-w-md rounded-2xl bg-white shadow-xl">
        <div className="border-b border-gray-200 px-6 py-4">
          <h2 id="ping-title" className="text-lg font-semibold text-gray-900">
            Quick Check-in
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            What are you doing right now?
          </p>
        </div>

        <div className="px-6 py-5">
          <div className="grid grid-cols-2 gap-2">
            {OPTIONS.map(({ value, label, icon: Icon, productive }) => (
              <button
                key={value}
                type="button"
                onClick={() => setSelected(value)}
                disabled={expired}
                className={`flex items-center gap-2 rounded-lg border px-3 py-3 text-left text-sm
                  transition-colors disabled:opacity-50 ${
                    selected === value
                      ? 'border-indigo-600 bg-indigo-50 text-indigo-900 ring-1 ring-indigo-600'
                      : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                  }`}
              >
                <Icon
                  className={`h-4 w-4 shrink-0 ${
                    productive ? 'text-indigo-600' : 'text-gray-400'
                  }`}
                />
                <span>{label}</span>
              </button>
            ))}
          </div>

          <label className="label mt-4" htmlFor="ping-note">
            What Are You Working On? <span className="text-gray-400">(optional)</span>
          </label>
          <input
            spellCheck={true}
            id="ping-note"
            className="input"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Calling Tata 1MG shortlist"
            maxLength={500}
            disabled={expired}
          />

          <p className="mt-3 flex items-center gap-1.5 text-xs text-gray-500">
            <MapPin className="h-3.5 w-3.5" />
            Your location is recorded with this check-in.
          </p>
        </div>

        <div className="flex items-center justify-between gap-3 rounded-b-2xl bg-gray-50 px-6 py-4">
          {expired ? (
            <span className="flex items-center gap-1.5 text-sm font-medium text-red-600">
              <AlertTriangle className="h-4 w-4" />
              This check-in has expired
            </span>
          ) : (
            <span
              className={`text-sm ${
                secondsLeft < 120 ? 'font-medium text-amber-600' : 'text-gray-500'
              }`}
            >
              {formatCountdown(secondsLeft)} left to respond
            </span>
          )}

          <button
            type="button"
            className="btn-primary"
            onClick={submit}
            disabled={!selected || submitting || expired}
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {submitting ? 'Saving' : 'Check in'}
          </button>
        </div>
      </div>
    </div>
  );
}

function remainingSeconds(ping: Ping): number {
  return Math.max(0, Math.floor((new Date(ping.expiresAt).getTime() - Date.now()) / 1000));
}

function formatCountdown(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}
